import { logger } from "@/shared/lib/logger";
import { z } from "zod";

export const TenantConfigSchema = z.object({
  tenantId: z.string().min(1),
  name: z.string().min(1),
  branding: z.object({
    appName: z.string(),
    logoPath: z.string(),
    faviconPath: z.string(),
  }).optional(),
  theme: z.object({
    primary: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    onPrimary: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    secondary: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    onSecondary: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    header: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    background: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    surface: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    surfaceAlt: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    text: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    textMuted: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    textDisabled: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    border: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    borderStrong: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    success: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    warning: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    error: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    info: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    onSemantic: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
  }).optional(),
  icons: z.object({
    trips: z.string().optional(),
    scheduling: z.string().optional(),
    reports: z.string().optional(),
    support: z.string().optional(),
  }).optional(),
  features: z.object({
    scheduling: z.boolean(),
    realTimeTracking: z.boolean(),
    reporting: z.boolean(),
    tripCancellation: z.boolean().optional(),
    showSpecialRequests: z.boolean().optional(),
  }),
  ui: z.object({
    showExperimentalFeatures: z.boolean().optional(),
    showAgencyNameInHeader: z.boolean().optional(),
  }).optional(),
  component: z.object({
    prepostverification: z.object({
      enabled: z.boolean().optional(),
    }).optional(),
  }).optional(),
  maps: z.object({
    defaultCenter: z.tuple([z.number(), z.number()]),
    defaultZoom: z.number().min(1).max(20),
  }).optional(),
  support: z.object({
    phone: z.string().optional(),
    email: z.string().email().optional(),
  }).optional(),
  legal: z.object({
    termsUrl: z.string().optional(),
    privacyUrl: z.string().optional(),
  }).optional(),
  apiEndpoint: z.string().optional(),
  convUiBaseUrl: z.string().optional(),
  mapConfig: z.object({
    center: z.tuple([z.number(), z.number()]),
    zoom: z.number().min(1).max(20),
    allowedZones: z.array(z.any()),
    restrictedZones: z.array(z.any()),
  }).optional(),
});

export type TenantConfig = z.infer<typeof TenantConfigSchema>;

// Runtime validation function
export const validateTenantConfig = (config: unknown): TenantConfig => {
  const dataToParse = typeof config === "string" ? JSON.parse(config) : config;
  const result = TenantConfigSchema.safeParse(dataToParse);
  if (!result.success) {
    logger.error("Tenant config validation failed:", result.error.flatten());
    throw new Error("Invalid tenant configuration");
  }
  return result.data;
};

// Default tenant configuration
export const getDefaultTenantConfig = (): TenantConfig => ({
  tenantId: "default",
  name: "Ecolane Evolution Demo",
  branding: {
    appName: "Ecolane Evolution Demo",
    logoPath: 'https://www.ecolane.com/hs-fs/hubfs/vertical-logo.png?t=1536266688057&width=216&name=vertical-logo.png',
    faviconPath: '/favicon.ico',
  },
  theme: {
    primary: '#0079C2',
    onPrimary: '#FFFFFF',
    secondary: '#64748b',
    onSecondary: '#FFFFFF',
    header: '#0C1F45',
    background: '#F8F9FA',
    surface: '#FFFFFF',
    text: '#212529',
    error: '#dc3545',
    info: '#17a2b8',
    success: '#28a745',
    warning: '#ffc107'
  },
  icons: {},
  features: {
    scheduling: true,
    realTimeTracking: true,
    reporting: true,
    tripCancellation: true,
    showSpecialRequests: true,
  },
  ui: {
    showAgencyNameInHeader: true,
  },
  apiEndpoint: "",
  mapConfig: {
    center: [40.7128, -74.0060],
    zoom: 12,
    allowedZones: [],
    restrictedZones: [],
  },
});
